import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Plus, Trash2, Star, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '@/components/ProtectedRoute';
import { PageMeta } from '@/components/PageMeta';
import { API } from '@/lib/api';


const newPlan = () => ({
  id: crypto.randomUUID(),
  name: 'Nouveau plan',
  price: '0',
  period: '/mois',
  popular: false,
  active: true,
  features: [''],
  order: 99,
});

const newCarnet = () => ({
  id: crypto.randomUUID(),
  name: 'Nouveau carnet',
  price: '0€',
  active: true,
  order: 99,
});

export default function AdminPricingPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pricing`);
        setData(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, []);

  const updatePlan = (idx, patch) => {
    const plans = [...data.plans];
    plans[idx] = { ...plans[idx], ...patch };
    // Only one plan can be popular
    if (patch.popular) {
      plans.forEach((p, i) => {
        if (i !== idx) p.popular = false;
      });
    }
    setData({ ...data, plans });
  };

  const updateFeature = (planIdx, fIdx, value) => {
    const plans = [...data.plans];
    const features = [...plans[planIdx].features];
    features[fIdx] = value;
    plans[planIdx] = { ...plans[planIdx], features };
    setData({ ...data, plans });
  };

  const addFeature = (planIdx) => {
    const plans = [...data.plans];
    plans[planIdx] = { ...plans[planIdx], features: [...plans[planIdx].features, ''] };
    setData({ ...data, plans });
  };

  const removeFeature = (planIdx, fIdx) => {
    const plans = [...data.plans];
    plans[planIdx] = { ...plans[planIdx], features: plans[planIdx].features.filter((_, i) => i !== fIdx) };
    setData({ ...data, plans });
  };

  const addPlan = () => setData({ ...data, plans: [...data.plans, newPlan()] });
  const removePlan = (idx) => setData({ ...data, plans: data.plans.filter((_, i) => i !== idx) });

  const updateCarnet = (idx, patch) => {
    const carnets = [...data.carnets];
    carnets[idx] = { ...carnets[idx], ...patch };
    setData({ ...data, carnets });
  };
  const addCarnet = () => setData({ ...data, carnets: [...data.carnets, newCarnet()] });
  const removeCarnet = (idx) => setData({ ...data, carnets: data.carnets.filter((_, i) => i !== idx) });

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Cleanup empty features + reindex order
      const plans = data.plans.map((p, i) => ({
        ...p,
        features: p.features.filter((f) => f.trim()),
        order: i + 1,
      }));
      const carnets = data.carnets.map((c, i) => ({ ...c, order: i + 1 }));
      await axios.put(`${API}/pricing`, { plans, carnets, note: data.note });
      toast.success('Tarifs enregistrés');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageMeta title="Tarifs admin" description="Gestion des tarifs" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Tarifs</span>
          <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2">
            OFFRES <span className="text-gradient-primary italic font-display inline-block pr-2">TARIFAIRES</span>
          </h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-shine bg-primary hover:bg-primary-light rounded-xl" data-testid="pricing-save-button">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER</>}
        </Button>
      </div>

      {/* Plans */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-dark tracking-wide">ABONNEMENTS MENSUELS</h2>
          <Button onClick={addPlan} variant="outline" size="sm" className="rounded-xl">
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter un plan
          </Button>
        </div>

        <div className="space-y-4">
          {data.plans.map((plan, idx) => (
            <Card key={plan.id} className={`p-5 rounded-2xl border-2 transition-all ${plan.popular ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`pricing-plan-${idx}`}>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="mb-1.5 block text-[10px] tracking-widest uppercase text-muted-foreground">Nom du plan</Label>
                  <Input value={plan.name} onChange={(e) => updatePlan(idx, { name: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[10px] tracking-widest uppercase text-muted-foreground">Prix</Label>
                  <Input value={plan.price} onChange={(e) => updatePlan(idx, { price: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[10px] tracking-widest uppercase text-muted-foreground">Période</Label>
                  <Input value={plan.period} onChange={(e) => updatePlan(idx, { period: e.target.value })} className="h-10 rounded-xl" placeholder="/mois" />
                </div>
              </div>

              <div className="mb-4">
                <Label className="mb-2 block text-[10px] tracking-widest uppercase text-muted-foreground">Caractéristiques (features)</Label>
                <div className="space-y-2">
                  {plan.features.map((f, fIdx) => (
                    <div key={fIdx} className="flex gap-2">
                      <Input value={f} onChange={(e) => updateFeature(idx, fIdx, e.target.value)} className="h-10 rounded-xl" placeholder="Ex: 3 cours par semaine" />
                      <Button type="button" variant="outline" size="sm" onClick={() => removeFeature(idx, fIdx)} className="rounded-xl">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => addFeature(idx)} className="rounded-xl w-full">
                    <Plus className="h-4 w-4 mr-1.5" /> Ajouter une feature
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch checked={plan.popular} onCheckedChange={(v) => updatePlan(idx, { popular: v })} data-testid={`pricing-plan-popular-${idx}`} />
                  <Label className="text-sm flex items-center gap-1">
                    <Star className="h-3.5 w-3.5" /> Plan populaire
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={plan.active} onCheckedChange={(v) => updatePlan(idx, { active: v })} />
                  <Label className="text-sm">Actif (visible sur le site)</Label>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => removePlan(idx)} className="ml-auto text-destructive hover:bg-destructive/10 hover:text-destructive rounded-xl">
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Supprimer
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Carnets */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-dark tracking-wide">CARNETS & DROP-IN</h2>
          <Button onClick={addCarnet} variant="outline" size="sm" className="rounded-xl">
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter un carnet
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {data.carnets.map((c, idx) => (
            <Card key={c.id} className="p-4 rounded-xl" data-testid={`pricing-carnet-${idx}`}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="mb-1 block text-[10px] tracking-widest uppercase text-muted-foreground">Nom</Label>
                  <Input value={c.name} onChange={(e) => updateCarnet(idx, { name: e.target.value })} className="h-9 rounded-lg text-sm" />
                </div>
                <div>
                  <Label className="mb-1 block text-[10px] tracking-widest uppercase text-muted-foreground">Prix</Label>
                  <Input value={c.price} onChange={(e) => updateCarnet(idx, { price: e.target.value })} className="h-9 rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={c.active} onCheckedChange={(v) => updateCarnet(idx, { active: v })} />
                  <Label className="text-xs">Actif</Label>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => removeCarnet(idx)} className="text-destructive hover:bg-destructive/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Note */}
      <Card className="p-5 rounded-2xl mb-10">
        <Label className="mb-2 block text-xs tracking-widest uppercase text-muted-foreground">Note affichée sous les tarifs</Label>
        <Textarea value={data.note || ''} onChange={(e) => setData({ ...data, note: e.target.value })} rows={2} className="rounded-xl" />
      </Card>

      <div className="sticky bottom-4 z-10">
        <Button onClick={handleSave} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-7 shadow-lg" size="lg">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER TOUTES LES MODIFICATIONS</>}
        </Button>
      </div>
    </AdminLayout>
  );
}
